
import { useState } from "react";
import { useClinics } from "@/hooks/useClinicData";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Users, Search } from "lucide-react";

interface JoinClinicFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export function JoinClinicForm({ onSuccess, onCancel }: JoinClinicFormProps) {
  const { toast } = useToast();
  const { data: clinics, isLoading: loadingClinics } = useClinics();
  const [selectedClinic, setSelectedClinic] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const filteredClinics = clinics?.filter(clinic =>
    clinic.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    clinic.city?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClinic) return;

    setIsSubmitting(true);
    try {
      // In a real implementation, this would send a request to join the clinic
      // For now, we'll show a message that the request has been sent
      toast({
        title: "Join Request Sent",
        description: "Your request to join the clinic has been sent. You'll be notified when it's approved.",
      });

      // For demo purposes, we'll call onSuccess after a short delay
      setTimeout(() => {
        onSuccess();
      }, 2000);
    } catch (error) {
      console.error('Error joining clinic:', error);
      toast({
        title: "Error",
        description: "Failed to send join request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onCancel}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-6 h-6 text-green-600" />
                Join Existing Clinic
              </CardTitle>
              <CardDescription>
                Search and request to join an existing clinic
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="search">Search Clinics</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <Input
                  id="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by clinic name or city..."
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="clinic">Select Clinic</Label>
              <Select value={selectedClinic} onValueChange={setSelectedClinic}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a clinic to join" />
                </SelectTrigger>
                <SelectContent>
                  {loadingClinics ? (
                    <SelectItem value="loading" disabled>Loading clinics...</SelectItem>
                  ) : filteredClinics.length === 0 ? (
                    <SelectItem value="no-clinics" disabled>No clinics found</SelectItem>
                  ) : (
                    filteredClinics.map((clinic) => (
                      <SelectItem key={clinic.id} value={clinic.id}>
                        <div>
                          <div className="font-medium">{clinic.name}</div>
                          {clinic.city && (
                            <div className="text-sm text-gray-500">{clinic.city}, {clinic.state}</div>
                          )}
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {selectedClinic && selectedClinic !== "loading" && selectedClinic !== "no-clinics" && (
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">What happens next?</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Your join request will be sent to the clinic administrators</li>
                  <li>• You'll receive an email notification when your request is reviewed</li>
                  <li>• Once approved, you'll gain access to the clinic's dashboard</li>
                </ul>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button type="submit" disabled={isSubmitting || !selectedClinic || selectedClinic === "loading" || selectedClinic === "no-clinics"}>
                {isSubmitting ? "Sending Request..." : "Send Join Request"}
              </Button>
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
